// Every call the Create page makes to the backends, one function per route (plan step F14;
// promptchan-backend docs/architecture/create-page.md). Each sends what the page has always sent: the same
// host, method, headers and body. Each returns fetch's Response untouched, because the page branches on the
// status (300, 452, 479 and the rest) and reads the body itself.
//
// Room 3's calls go through the doorway (src/lib/api/doorway.ts; ai_app_backend plan task r3-rewire): the pictures,
// edits, keeping and deleting in the `create` area, the video panel's in `create_video`, each with its own switch,
// both off. With an area off, a call sends exactly the request it sent before (tests/create-api.test.mjs). Keeping,
// publishing and deleting are shared with the gallery, the profile, the picture page and chat, so v2's pictures go to
// v2 from every screen. The bookmarks (saved prompts) and thumbs are in the `create` area too (r3-saved-prompts); the
// measurements still go straight to v1, and the folder lists are folders.ts's.
//
// `token` is the person's Firebase ID token. The backends read it raw from Authorization, with no
// "Bearer". The headers differ from route to route only because they always have; making them uniform is a
// change of its own.
import { call, lazySide } from '../../../lib/api/doorway.ts'
import type { Area, V2Side } from '../../../lib/api/doorway.ts'
import { myFolders } from '../../../lib/api/folders.ts'
import { isV2Media, videoFromV2Media } from '../../../lib/api/media-ids.ts'

const CLOUD = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD
const FREE = process.env.NEXT_PUBLIC_PROMPTCHAN_FREE
const VIDEO = process.env.NEXT_PUBLIC_PROMPTCHAN_VIDEO

type Body = Record<string, unknown>

const jsonHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: token,
})

/** A call of room 3's on the doorway: `url` is its whole v1 address, as the page always built it. */
const viaDoorway = (area: Area, url: string, init: RequestInit) =>
  call(area, url, init)

const postInit = (token: string, body: Body): RequestInit => ({
  method: 'POST',
  headers: jsonHeaders(token),
  body: JSON.stringify(body),
})

const postVia = (area: Area, url: string, token: string, body: Body) =>
  viaDoorway(area, url, postInit(token, body))

type Sides = typeof import('../../../lib/api/create-v2.ts')

/**
 * A call's v2 side from src/lib/api/create-v2.ts, loaded only when the switch puts the visitor on v2; `use` is given
 * the same call on v1, for a request v2 can't take as sent. A module that fails to load sends the call to v1.
 */
const sideOf =
  (
    url: string,
    use: (loaded: Sides, v1: () => Promise<Response>) => V2Side,
  ): V2Side =>
  (token, init) =>
    lazySide(
      () => import('../../../lib/api/create-v2.ts'),
      (loaded) => use(loaded, () => fetch(url, init)),
      url,
    )(token, init)

/**
 * Whether an image request stays on v1 whatever the `create` area's switch says: none does now. A custom pose picture
 * (a control image) is v2's on every model (its `pose_reference`: Flame's by plan task r3-pose, the older models' by
 * r3-v1-kinds, with `stick_mode`), and so is a casual (chill) request since ai_app_backend's 5610 (`casual_picture`;
 * plan task r3-v1-kinds). Kept as the one place a request is held on v1 should one need to be again.
 */
export function staysOnV1(body: Body): boolean {
  void body
  return false
}

// Image generation

/**
 * The backend an image request goes to: Free accounts and any request with an AI Character use the free
 * pool's host, paid accounts the main one. (Both are one backend since September 2026; the hosts still
 * differ.) A remembered job is collected from the same host (src/lib/image-jobs.js).
 */
export const imageBackend = ({
  paid,
  withCharacter,
}: {
  paid: boolean
  withCharacter: boolean
}) => (!paid || withCharacter ? FREE : CLOUD)

/** Generates images. Flame models have their own route on the same backend. */
export const generateImage = (
  apiBase: string | undefined,
  model: string,
  token: string,
  body: Body,
) => {
  const url = `${apiBase}${model.includes('Flame') ? '/api/generate_image' : '/api/generate'}`
  return call(
    'create',
    url,
    postInit(token, body),
    staysOnV1(body)
      ? undefined
      : {
          token,
          side: sideOf(url, (loaded, v1) => (t) => loaded.picture(t, body, v1)),
        },
  )
}

/** Edits an image with a text instruction (Qwen-Edit). */
export const tweakImage = (token: string, body: Body) => {
  const url = `${CLOUD}/api/tweak_image`
  return call('create', url, postInit(token, body), {
    token,
    side: sideOf(url, (loaded, v1) => (t) => loaded.edit(t, body, v1)),
  })
}

// Video, on the video machines (submit, status, result) and the main backend (the list, refunds). With the
// `create_video` area on, a new clip or a longer clip is v2's (src/lib/api/video-v2.ts), and an order v2 holds (its id
// `o_…`) is followed on v2 whatever the switch says; v1's jobs stay v1's.

type VideoSides = typeof import('../../../lib/api/video-v2.ts')

/** A video call's v2 side from video-v2.ts, loaded only when used; `v1` is the same call on v1. */
const videoSide =
  (
    url: string,
    use: (loaded: VideoSides, v1: () => Promise<Response>) => V2Side,
  ): V2Side =>
  (token, init) =>
    lazySide(
      () => import('../../../lib/api/video-v2.ts'),
      (loaded) => use(loaded, () => fetch(url, init)),
      url,
    )(token, init)

/** Whether a request id names one of v2's orders (`o_…`): followed on v2 whatever the switch says. */
const isV2Order = (id: string) => id.startsWith('o_')

/** A new clip or a longer clip; one made from v2's own picture or clip is v2's whatever the switch says (v1 can't find it). */
export const submitVideo = (token: string, body: Body) => {
  const url = `${VIDEO}/api/video_v3.1/submit`
  return call('create_video', url, postInit(token, body), {
    token,
    ownedByV2: videoFromV2Media(body),
    side: videoSide(url, (loaded, v1) => (t) => loaded.submit(t, body, v1)),
  })
}

/** One of the polls, for a request id: v2's order on v2, v1's job on v1. */
const poll =
  (
    path: 'status' | 'status_with_logs' | 'result',
    use: (
      loaded: VideoSides,
    ) => (token: string, id: string) => Promise<Response>,
  ) =>
  (token: string, requestId: string) => {
    const url = `${VIDEO}/api/video_v3.1/${path}/${requestId}`
    return call(
      'create_video',
      url,
      { headers: { Authorization: token } },
      isV2Order(requestId)
        ? {
            token,
            ownedByV2: true,
            side: videoSide(url, (loaded) => (t) => use(loaded)(t, requestId)),
          }
        : undefined,
    )
  }

export const videoStatus = poll('status', (loaded) => loaded.status)

export const videoStatusWithLogs = poll(
  'status_with_logs',
  (loaded) => loaded.statusWithLogs,
)

export const videoResult = poll('result', (loaded) => loaded.result)

/**
 * The person's recent video jobs, for the history panel: on v2, v2's clips first, then v1's own jobs. `withV1` false
 * (Create's load, before the panel opens): on v2, v2's clips alone, and v1's list not asked.
 */
export const listVideoJobs = (token: string, withV1 = true) => {
  const url = `${CLOUD}/api/video_v3.1/video_status`
  return call(
    'create_video',
    url,
    { method: 'GET', headers: jsonHeaders(token) },
    {
      token,
      side: videoSide(
        url,
        (loaded, v1) => (t) => loaded.history(t, withV1 ? v1 : null),
      ),
    },
  )
}

/** Asks for the gems of a failed video back. */
export const claimFailedVideo = (token: string, requestId: string) =>
  viaDoorway(
    'create_video',
    `${CLOUD}/api/video_v3.1/claim_failed/${requestId}`,
    {
      method: 'POST',
      headers: jsonHeaders(token),
    },
  )

// Keeping results

export type PublishBody = {
  /** An image: its URL or base64. A video: its document id. */
  image: string
  folder: string | null
  visibility: 'Public' | 'Private'
}

/**
 * Keeps (Private) or publishes (Public) a result. `mediaId` is the result's id when the page has it: one of v2's goes
 * to v2 by its id whatever the switch says, so v1's publish never acts on v2's rows without v2's rules (can_keep, held
 * back, the words kept: plan.pm r3-rewire's guards); the id is never sent to v1.
 */
export const publish = (
  token: string,
  kind: 'image' | 'video',
  body: PublishBody,
  mediaId?: string | null,
) => {
  const url = `${CLOUD}/api/${kind === 'video' ? 'publish_video' : 'publish_image'}`
  return call(
    kind === 'video' ? 'create_video' : 'create',
    url,
    postInit(token, body),
    isV2Media(mediaId)
      ? {
          token,
          ownedByV2: true,
          side: sideOf(
            url,
            (loaded) => (t, init) =>
              loaded.keep(t, init, mediaId, body.visibility, body.folder),
          ),
        }
      : undefined,
  )
}

/**
 * A picture order on v2 remembered from before a reload (src/lib/image-jobs.js `noteOrder`), followed to its end and
 * answered as the page's recovery answers v1's job, or null. v2's whatever the switch says: only v2 knows the order.
 */
export const recoverOrder = async (token: string, orderId: string) => {
  try {
    const loaded = await import('../../../lib/api/create-v2.ts')
    return await loaded.recoverPicture(token, orderId)
  } catch {
    return null
  }
}

/**
 * Deletes an image or a video: both are in the `images` collection; one of v2's by its id on v2, whatever the switch,
 * one of v1's on v2 with the `create` area.
 */
export const deleteMedia = (
  token: string,
  body: { imageId: string; imageUid: string },
) => {
  const url = `${CLOUD}/api/delete_image`
  return call(
    'create',
    url,
    {
      method: 'DELETE',
      headers: jsonHeaders(token),
      body: JSON.stringify(body),
    },
    {
      token,
      // One of v2's goes to v2 whatever the switch says; one of v1's with the area (v2 deletes one's own kept items
      // v1 made, as v1's delete_image: plan task v1-made-items).
      ownedByV2: isV2Media(body.imageId),
      side: sideOf(
        url,
        (loaded) => (t, init) => loaded.remove(t, init, body.imageId),
      ),
    },
  )
}

// The gallery's and profile's calls for several of the person's pictures at once (Move to public, Delete; plan.pm
// r3-rewire2, R3-40). v2's pictures and clips are rows in v1's own table, so v1's lists show the kept ones, and v1's
// routes would publish or delete them without v2's rules (can_keep, held back, its delete's own bookkeeping). So v1's
// ids go to v1 in one request, exactly as the screens sent it (Move to public: with the `create` area, to v2 by their
// ids, plan task v1-made-items), and each of v2's (`m_…`) goes to v2 by its id,
// whatever the switch says (`publish` and `deleteMedia` above); the answer keeps v1's shape. v2 has no bulk call.

const asV1 = (body: Body) =>
  new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  })

/** Splits ids into v1's and v2's (`isV2Media`). */
const byOwner = (ids: string[]) => ({
  v1Ids: ids.filter((id) => !isV2Media(id)),
  v2Ids: ids.filter((id) => isV2Media(id)),
})

/**
 * Moves private pictures and clips to public, answered as v1's /api/private_to_public: `{ status, message,
 * rejected_ids }`. v1's go to v1, or with the `create` area to v2 by their ids. One of v2's that v2 refuses to publish is rejected, as v1 rejects its own; a sign-in or server
 * failure is answered as it came.
 */
export async function moveToPublic(
  token: string,
  ids: string[],
): Promise<Response> {
  const { v1Ids, v2Ids } = byOwner(ids)
  const url = `${CLOUD}/api/private_to_public`
  // v1's ids in one request, as the screens sent it; with the `create` area, each made public on v2 (plan task
  // v1-made-items), a clip v2 can't check yet sent back to v1 (`makePublic`).
  const v1 =
    v1Ids.length || !v2Ids.length
      ? await call('create', url, postInit(token, { images: v1Ids }), {
          token,
          side: sideOf(
            url,
            (loaded) => (t, init) =>
              loaded.makePublic(t, init, v1Ids, (rest) =>
                fetch(url, { ...init, body: JSON.stringify({ images: rest }) }),
              ),
          ),
        })
      : null
  if (v1 && (!v2Ids.length || !v1.ok)) return v1
  const rejected: string[] = v1
    ? ((await v1.json().catch(() => ({})))?.rejected_ids ?? [])
    : []
  for (const id of v2Ids) {
    const answer = await publish(
      token,
      'image',
      { image: id, folder: null, visibility: 'Public' },
      id,
    )
    if (answer.status === 401 || answer.status >= 500) return answer
    if (!answer.ok) rejected.push(id)
  }
  return asV1({
    status: 'success',
    message: 'Images moved to public successfully',
    rejected_ids: rejected,
  })
}

/**
 * Deletes pictures and clips, answered as v1's /api/delete_images. One of v2's already gone (404) is skipped, as v1
 * skips an id it can't delete; any other failure is answered as it came.
 */
export async function deleteMany(
  token: string,
  ids: string[],
): Promise<Response> {
  const { v1Ids, v2Ids } = byOwner(ids)
  const v1 =
    v1Ids.length || !v2Ids.length
      ? await fetch(`${CLOUD}/api/delete_images`, {
          method: 'DELETE',
          headers: jsonHeaders(token),
          body: JSON.stringify({ images: v1Ids }),
        })
      : null
  if (v1 && (!v2Ids.length || !v1.ok)) return v1
  for (const id of v2Ids) {
    const answer = await deleteMedia(token, { imageId: id, imageUid: '' })
    if (!answer.ok && answer.status !== 404) return answer
  }
  return asV1({ status: 'success', message: 'Images deleted successfully' })
}

// Create's bookmarks (the saved prompts) and thumbs (plan task r3-saved-prompts): in the `create` area, so a visitor
// it puts on v2 saves, lists, names and deletes them on v2. They are one list in v1's own table either way. A thumb or
// an early-access note on one of v2's results (`m_…`) goes to v2 with the area; one of v1's stays on v1, which knows
// its Free pictures that have no record. Since v2 takes one's own items v1 made (plan task v1-made-items), a thumb or
// note on one of v1's goes to v2 with the area too.

/** Saves a result's prompt and settings as a workflow (the Bookmark tab). */
export const savePrompt = (token: string, imageId: string) => {
  const url = `${CLOUD}/api/save_prompt`
  return call('create', url, postInit(token, { image_id: imageId }), {
    token,
    side: sideOf(
      url,
      (loaded) => (t, init) => loaded.savePrompt(t, init, imageId),
    ),
  })
}

/** The person's saved prompts, for the Bookmark tab: pictures', clips' (`video`), or both. */
export const listSavedPrompts = (token: string, video?: boolean) => {
  const url = new URL(`${CLOUD}/api/get_saved_prompts`)
  if (typeof video === 'boolean') url.searchParams.set('video', String(video))
  return call(
    'create',
    url.toString(),
    { method: 'GET', headers: jsonHeaders(token) },
    {
      token,
      side: sideOf(
        url.toString(),
        (loaded) => (t, init) =>
          loaded.savedPrompts(
            t,
            init,
            typeof video === 'boolean' ? video : undefined,
          ),
      ),
    },
  )
}

/** Names a saved prompt. */
export const renameSavedPrompt = (
  token: string,
  promptId: string,
  name: string,
) => {
  const url = `${CLOUD}/api/rename_prompt/${promptId}`
  return call('create', url, postInit(token, { name }), {
    token,
    side: sideOf(
      url,
      (loaded) => (t, init) => loaded.renamePrompt(t, init, promptId, name),
    ),
  })
}

/** Deletes a saved prompt. */
export const deleteSavedPrompt = (token: string, promptId: string) => {
  const url = `${CLOUD}/api/delete_prompt/${promptId}`
  return call(
    'create',
    url,
    { method: 'DELETE', headers: jsonHeaders(token) },
    {
      token,
      side: sideOf(
        url,
        (loaded) => (t, init) => loaded.deletePrompt(t, init, promptId),
      ),
    },
  )
}

/** The person's public folders (src/lib/api/folders.ts: room 3's, on v2 with the area's switch). */
export const publicFolders = (token: string) => myFolders(token, 'public')

/** The person's private folders (src/lib/api/folders.ts). */
export const privateFolders = (token: string) => myFolders(token, 'private')

// Measurement: the caller never waits on these or shows their failure.

const VALUES = ['up', 'down'] as const
const ACTIONS = [
  'download',
  'create_character',
  'use_character',
  'publish',
  'publish_video',
  'share',
  'save',
] as const

/**
 * A thumb up or down on a result (backend myapp/output_feedback.py): on v2 with the `create` area, whichever API made
 * it (v2 takes one's own items v1 made too: plan task v1-made-items); one sent without its id stays on v1.
 */
export const sendOutputFeedback = (token: string, body: Body) => {
  const url = `${CLOUD}/api/output_feedback`
  const mediaId = typeof body.media_id === 'string' ? body.media_id : null
  const value = VALUES.find((v) => v === body.value)
  return call(
    'create',
    url,
    postInit(token, body),
    mediaId && value
      ? {
          token,
          side: sideOf(
            url,
            (loaded) => (t, init) => loaded.rate(t, init, mediaId!, value),
          ),
        }
      : undefined,
  )
}

/** Someone tried to keep an early-access result (backend myapp/early_access.py); on v2 with the area, as the thumb. */
export const reportEarlyAccessBlocked = (token: string, body: Body) => {
  const url = `${CLOUD}/api/early_access/blocked`
  const mediaId = typeof body.media_id === 'string' ? body.media_id : null
  const action = ACTIONS.find((a) => a === body.action)
  return call(
    'create',
    url,
    postInit(token, body),
    mediaId && action
      ? {
          token,
          side: sideOf(
            url,
            (loaded) => (t, init) =>
              loaded.earlyAccessAttempt(t, init, mediaId!, action),
          ),
        }
      : undefined,
  )
}
